import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useHistory, useParams } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { userAtom, notificationsAtom } from '../../../atoms/Atoms';
import { H2, H4 } from '../../shared-components/typography/Title';
import backdrop from '../../../assets/images/misc/login_backdrop.png';
import Button from '../../shared-components/buttons/Button';
import Form from '../../shared-components/form/Form';
import Input from '../../shared-components/form/Input';
import Logo from '../../shared-components/logo/Logo';
import api from '../../../api/api';
import envConfig from '../../../envConfig';
import { encryptString } from '../../../functions';

function UpdatePass() {
    const [storageUser] = useLocalStorage(`${envConfig.StoragePrefix}-user`);
    const [impersonate] = useLocalStorage(`${envConfig.StoragePrefix}-impersonate`);
    const [notifications, setNotifications] = useRecoilState(notificationsAtom);
    const { t } = useTranslation();
    const params = useParams();
    const history = useHistory();
    const user = useRecoilValue(userAtom);
    const [loading, setLoading] = useState(false);

    if (!storageUser) {
      window.location = '/';
    }

    useEffect(() => {
      if (user) {
        if (!user?.forcePasswordReset && !impersonate) {
            window.location = '/home';
        }
      }
    }, [user]);

    function submit(values) {
      setLoading(true);
      api.post('auth/password/update', {
        CurrentPassword: encryptString(values.currentpassword),
        NewPassword: encryptString(values.password),
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setNotifications([
            ...notifications,
            {
              title: t('yay'),
              description: response.data.message,
              error: false,
            },
          ]);
          history.push('/');
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
    }

    const passwordFormik = useFormik({
      initialValues: {
        currentpassword: '',
        password: '',
        passwordConfirmation: ''
      },
      validationSchema: Yup.object().shape({
        currentpassword: Yup.string().required(t('required_field')),
        password: Yup.string().required(t('required_field')),
        passwordConfirmation: Yup.string()
        .oneOf(
          [Yup.ref('password'), null],
          t('same_password')
        )
        .required(t('required_field')),
      }),
      onSubmit: (values, { setSubmitting }) => submit(values),
    });

    return (
      <div className="grid mx-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-6">
        <div className="flex col-span-1 lg:col-span-4 flex-col w-full mt-12 md:mt-48 justify-center items-center">
          <img className="hidden md:block bottom-0 mt-auto" alt={t('welcome_image_alt')} src={backdrop} />
        </div>
        <div className="flex md:col-span-1 lg:col-span-2 flex-col w-full md:mt-48 justify-center items-center">
          <Logo
            className="w-64 hover:scale-110"
          />
          <H2 className="my-4 text-center" text={t('enter_your_new_password_below')} />
          <Form className="mt-4 w-10/12">
            <Input
              label={t('current_password')}
              required
              id="currentpassword"
              name="currentpassword"
              type="password"
              className="mb-8"
              autoComplete="password"
              value={passwordFormik.values.currentpassword}
              onChange={passwordFormik.handleChange}
              error={
                passwordFormik.errors.password &&
                passwordFormik.touched.password &&
                passwordFormik.errors.password
              }
            />
            <Input
              label={t('new_password')}
              required
              id="password"
              name="password"
              type="password"
              className="mb-8"
              value={passwordFormik.values.password}
              onChange={passwordFormik.handleChange}
              error={
                passwordFormik.errors.password &&
                passwordFormik.touched.password &&
                passwordFormik.errors.password
              }
            />
            <Input
              label={t('confirm_password')}
              required
              id="passwordConfirmation"
              name="passwordConfirmation"
              type="password"
              value={passwordFormik.values.passwordConfirmation}
              onChange={passwordFormik.handleChange}
              error={
                passwordFormik.errors.passwordConfirmation &&
                passwordFormik.touched.passwordConfirmation &&
                passwordFormik.errors.passwordConfirmation
              }
            />
          </Form>
          <Button
            className="bg-primaryColor mt-8 mb-6 w-9/12"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            loading={loading}
            text={t('submit')}
            onClick={passwordFormik.submitForm}
          />
        </div>
        <div className="flex md:hidden col-span-1 flex-col w-full mb-4 justify-center items-center">
          <img className="bottom-0 mt-auto" alt={t('welcome_image_alt')} src={backdrop} />
        </div>
      </div>
    );
}

export default UpdatePass;
